// Utilitários de máscara, validação e hash de senha
// Autenticação própria via CPF + Senha. Sem OTP, sem Lumi Auth.

export function nowISO(): string {
  return new Date().toISOString()
}

const HASH_PREFIX = 'sha256$'
const SALT = 'protocoleai-salt-v1'

export async function hashPassword(password: string): Promise<string> {
  const enc = new TextEncoder().encode(SALT + password)
  const buf = await crypto.subtle.digest('SHA-256', enc)
  const hex = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
  return `${HASH_PREFIX}${hex}`
}

export async function verifyPassword(password: string, hash: string): Promise<boolean> {
  if (!hash || !hash.startsWith(HASH_PREFIX))
    return false
  const computed = await hashPassword(password)
  return computed === hash
}

export function isLegacyHash(hash: string): boolean {
  return !!hash && !hash.startsWith(HASH_PREFIX)
}

// ---------- CPF ----------

export function unmaskCPF(value: string): string {
  return (value || '').replace(/\D/g, '')
}

export function maskCPF(value: string): string {
  const digits = unmaskCPF(value).slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

export function isValidCPF(cpfRaw: string): boolean {
  const cpf = unmaskCPF(cpfRaw)
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf))
    return false

  let sum = 0
  for (let i = 0; i < 9; i++) sum += Number.parseInt(cpf[i], 10) * (10 - i)
  let rest = (sum * 10) % 11
  if (rest === 10) rest = 0
  if (rest !== Number.parseInt(cpf[9], 10))
    return false

  sum = 0
  for (let i = 0; i < 10; i++) sum += Number.parseInt(cpf[i], 10) * (11 - i)
  rest = (sum * 10) % 11
  if (rest === 10) rest = 0
  if (rest !== Number.parseInt(cpf[10], 10))
    return false

  return true
}

// ---------- CNPJ ----------

export function unmaskCNPJ(value: string): string {
  return (value || '').replace(/\D/g, '')
}

export function maskCNPJ(value: string): string {
  const digits = unmaskCNPJ(value).slice(0, 14)
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
}

export function isValidCNPJ(cnpjRaw: string): boolean {
  const cnpj = unmaskCNPJ(cnpjRaw)
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj))
    return false

  const calc = (base: string, weights: number[]) => {
    const sum = base.split('').reduce((acc, digit, idx) => acc + Number.parseInt(digit, 10) * weights[idx], 0)
    const rest = sum % 11
    return rest < 2 ? 0 : 11 - rest
  }

  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

  const d1 = calc(cnpj.slice(0, 12), w1)
  if (d1 !== Number.parseInt(cnpj[12], 10))
    return false

  const d2 = calc(cnpj.slice(0, 13), w2)
  if (d2 !== Number.parseInt(cnpj[13], 10))
    return false

  return true
}

// ---------- Telefone (Brasil) ----------

export function unmaskPhone(value: string): string {
  return (value || '').replace(/\D/g, '')
}

export function maskPhone(value: string): string {
  const digits = unmaskPhone(value).slice(0, 11)
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2')
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2')
}

// ---------- Datas ----------

export function formatDateBR(iso?: string): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('pt-BR')
}

/** Formata datas guardadas ao meio-dia UTC sem recuar um dia pelo fuso horário. */
export function formatDateUTCBR(iso?: string): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}

export function formatDateTimeBR(iso?: string): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

// ---------- Protocolo ----------

export function generateProtocolNumber(): string {
  const numbers = Math.floor(10000 + Math.random() * 90000).toString()
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const letter = letters[Math.floor(Math.random() * letters.length)]
  return `${numbers}${letter}`
}
